import React, { useState, useEffect } from "react";
import "./CoachingPage.css";
import { apiGet, apiPost } from "../../services/api";
import { useNavigate } from "react-router-dom";
import { CoachingPlan, Session } from "../../types";
import { SessionDetailPanel } from "../shared/SessionDetailPanel";

export function CoachingPage() {
  const navigate = useNavigate();
  const [plan, setPlan] = useState<CoachingPlan | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [noPlan, setNoPlan] = useState(false);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [planResult, sessionsResult] = await Promise.all([
        apiGet<CoachingPlan | { plan: null; message: string }>("/api/coaching/plan"),
        apiGet<{ items: Session[] }>("/api/sessions"),
      ]);
      if ("plan" in planResult && planResult.plan === null) {
        setNoPlan(true);
      } else {
        setPlan(planResult as CoachingPlan);
      }
      setSessions(sessionsResult.items);
    } catch (err) {
      console.error("Failed to load coaching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const generatePlan = async () => {
    setGenerating(true);
    try {
      const result = await apiPost<CoachingPlan>("/api/coaching/plan/generate");
      setPlan(result);
      setNoPlan(false);
    } catch (err) {
      console.error("Failed to generate coaching plan:", err);
    } finally {
      setGenerating(false);
    }
  };

  const recentSessions = sessions.slice(0, 5);
  const formatDate = (iso: string) => new Date(iso).toLocaleDateString();
  const formatDuration = (ms: number) => {
    if (!ms) return "—";
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  if (loading) return <div className="protrain-coaching-loading">Loading coaching plan...</div>;

  if (noPlan) {
    return (
      <div className="protrain-coaching-page">
        <h2 className="protrain-page-title">Coaching Plan</h2>
        <div className="protrain-coaching-empty">
          <p>No coaching plan yet. Complete some role-play sessions first, then generate your plan.</p>
          <button className="protrain-coaching-generate-btn" onClick={generatePlan} disabled={generating}>
            {generating ? "Generating..." : "✨ Generate Coaching Plan"}
          </button>
        </div>

        {recentSessions.length > 0 && (
          <div className="protrain-coaching-section">
            <h3>Recent Sessions</h3>
            <div className="protrain-coaching-session-list">
              {recentSessions.map((s) => (
                <button key={s.sessionId} className="protrain-coaching-session-row" onClick={() => setSelectedSession(s)}>
                  <span>{s.mode === "text" ? "💬" : s.mode === "transcript" ? "📄" : "🎤"}</span>
                  <span>{formatDate(s.createdAt)}</span>
                  <span className="protrain-coaching-session-persona">{s.personaType}</span>
                  <span>{formatDuration(s.durationMs)}</span>
                  <span className="protrain-coaching-session-score">
                    {s.score ? s.score.overallScore : "—"}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <SessionDetailPanel
          session={selectedSession}
          onClose={() => setSelectedSession(null)}
          onPracticeAgain={(scenarioId) => {
            setSelectedSession(null);
            navigate(`/practice?scenario=${scenarioId}`);
          }}
        />
      </div>
    );
  }

  return (
    <div className="protrain-coaching-page">
      <h2 className="protrain-page-title">Coaching Plan</h2>

      {plan && (
        <>
          <div className="protrain-coaching-header-row">
            <div className="protrain-coaching-score-card">
              <div className="protrain-coaching-score-value">{plan.strengthScore}</div>
              <div className="protrain-coaching-score-label">Strength Score</div>
              <div className="protrain-coaching-score-sessions">{plan.sessionsAnalyzed} sessions analyzed</div>
            </div>
            <button className="protrain-coaching-generate-btn" onClick={generatePlan} disabled={generating}>
              {generating ? "Regenerating..." : "🔄 Regenerate Plan"}
            </button>
          </div>

          <div className="protrain-coaching-section">
            <h3>Improvement Areas</h3>
            <div className="protrain-coaching-areas">
              {plan.improvementAreas.map((area, i) => (
                <div key={i} className="protrain-coaching-area-card">
                  <div className="protrain-coaching-area-header">
                    <span className="protrain-coaching-area-name">{area.area}</span>
                    <span className="protrain-coaching-area-score">{area.currentScore} → {area.targetScore}</span>
                  </div>
                  <div className="protrain-coaching-area-progress-bar">
                    <div className="protrain-coaching-area-progress-fill" style={{ width: `${area.progress}%` }} />
                  </div>
                  <ul className="protrain-coaching-area-tips">
                    {area.tips.map((tip, j) => (
                      <li key={j}>{tip}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="protrain-coaching-section">
            <h3>Recommended Scenarios</h3>
            <div className="protrain-coaching-scenarios">
              {plan.recommendedScenarios.map((rec, i) => (
                <div key={i} className="protrain-coaching-rec-card">
                  <div>
                    <span className="protrain-coaching-rec-name">{rec.scenarioName}</span>
                    <span className="protrain-coaching-rec-reason">{rec.reason}</span>
                  </div>
                  <button
                    className="protrain-coaching-practice-btn"
                    onClick={() => navigate(`/practice?scenario=${rec.scenarioId}`)}
                  >
                    🎭 Practice This
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Recent Sessions */}
      <div className="protrain-coaching-section">
        <h3>Recent Sessions</h3>
        {recentSessions.length === 0 ? (
          <p className="protrain-coaching-empty-text">No sessions yet. Start practicing to see your history here.</p>
        ) : (
          <div className="protrain-coaching-session-list">
            {recentSessions.map((s) => (
              <button key={s.sessionId} className="protrain-coaching-session-row" onClick={() => setSelectedSession(s)}>
                <span>{s.mode === "text" ? "💬" : s.mode === "transcript" ? "📄" : "🎤"}</span>
                <span>{formatDate(s.createdAt)}</span>
                <span className="protrain-coaching-session-persona">{s.personaType}</span>
                <span>{formatDuration(s.durationMs)}</span>
                <span className="protrain-coaching-session-score">
                  {s.score ? s.score.overallScore : "—"}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <SessionDetailPanel
        session={selectedSession}
        onClose={() => setSelectedSession(null)}
        onPracticeAgain={(scenarioId) => {
          setSelectedSession(null);
          navigate(`/practice?scenario=${scenarioId}`);
        }}
      />
    </div>
  );
}
